<script setup>
import { ref } from "vue";
import { useBreakpoint } from "@/composables/useBreakpoint";
import Popover from "primevue/popover";
import Button from "primevue/button";
import Accordion from "primevue/accordion";
import AccordionPanel from "primevue/accordionpanel";
import AccordionHeader from "primevue/accordionheader";
import Badge from "primevue/badge";
import AccordionContent from "primevue/accordioncontent";
import BotaoVoltar from "./BotaoVoltar.vue";
import BotaoMenuLateral from "./BotaoMenuLateral.vue";
import MobileHeader from "./MobileHeader.vue";

const props = defineProps({
    titulo: {
        type: String,
        description: 'Título do header'
    },
    subtitulo: {
        type: String,
        description: 'Subtítulo do header'
    },
    icon: {
        type: String,
        description: 'Ícone que ficará na esquerda do título e do subtítulo'
    },
    showMobileHeader: {
        type: Boolean,
        default: true,
        description: 'Se deve exibir o MobileHeader'
    },
    tituloMobileHeader: {
        type: String,
        description: 'Título do MobileHeader'
    },
    subTituloMobileHeader: {
        type: String,
        description: 'Subtítulo do MobileHeader'
    },
    accordion: {
        type: Boolean,
        default: false,
        description: 'Se header deve ser Accordion quando mobile'
    },
    accordionTitle: {
        type: String,
        default: 'Pesquisar',
        description: 'Título do Accordion quando mobile'
    },
    openAccordionOnStart: {
        type: Boolean,
        default: false,
        description: 'Se o Accordion deve estar aberto ao iniciar'
    },
    accordionBagdeValue: {
        type: Number,
        default: 0,
        description: 'Valor (Badge) a ser exibido ao lado do título do Accordion'
    },
    popover: {
        type: Boolean,
        default: false,
        description: 'Se slot do Header deve ser um Popover quando desktop'
    },
    linkSaibaMais: {
        type: String,
        description: 'Link de Saiba Mais que fica à direita do título'
    }
});

// ACCORDION
const activeAccordion = ref(props.openAccordionOnStart ? '1' : '0');

const closeAccordion = () => activeAccordion.value = '0';

// POPOVER
const popover = ref();

const togglePopover = (e) => popover.value.toggle(e);

const hidePopover = () => popover.value.hide();

// MOBILE/DESKTOP
const { isMobile } = useBreakpoint();
//

const acessarLinkSaibaMais = () => {
    window.location.href = props.linkSaibaMais;
}

defineExpose({ closeAccordion, togglePopover, hidePopover });
</script>

<template>
    <header class="header-container">
        <MobileHeader
            v-if="isMobile && props.showMobileHeader"
            :titulo="props.tituloMobileHeader ?? props.titulo"
            :subtitulo="props.subTituloMobileHeader ?? props.subtitulo"
        />

        <!-- DESKTOP -->
        <div v-if="!isMobile" class="header desktop-header">
            <div class="w-100 d-flex align-items-center justify-content-between gap-3">
                <div class="d-flex align-items-center justify-content-between gap-3">
                    <div class="d-flex align-items-center gap-2">
                        <BotaoVoltar
                            :icon-style="{ color: 'var(--color-text-light)' }"
                            button-hover-color="var(--color-primary-hover)"
                        />

                        <div v-if="props.icon" class="icon-container">
                            <i :class="props.icon"/>
                        </div>

                        <div class="d-flex flex-column gap-1">
                            <h2 class="header-title" :style="{ fontSize: isMobile ? '1.2rem' : '' }">
                                {{ props.titulo }}
                            </h2>

                            <span v-if="props.subtitulo" style="font-weight: 300;">
                                {{ props.subtitulo }}
                            </span>
                        </div>

                        <i
                            v-if="props.linkSaibaMais"
                            v-tooltip="'Saiba Mais'"
                            class="pi pi-question-circle icon-link-saiba-mais"
                            @click="acessarLinkSaibaMais()"
                        />
                    </div>
                </div>

                <slot v-if="!props.popover"/>

                <!-- POPOVER -->
                <Button
                    v-else
                    label="Pesquisar"
                    icon="pi pi-search"
                    severity="secondary"
                    :badge="props.accordionBagdeValue ? props.accordionBagdeValue.toString() : null"
                    badge-severity="danger"
                    @click="togglePopover($event)"
                />

                <Popover ref="popover">
                    <slot/>
                </Popover>
                <!-- FIM POPOVER -->
            </div>
        </div>
        <!-- FIM DESKTOP -->

        <!-- MOBILE -->
        <div
            v-if="isMobile && !props.accordion"
            class="header"
            :style="{ borderRadius: props.showMobileHeader ? '8px' : '0 0 8px 8px' }"
        >
            <div class="w-100 p-3 d-flex flex-column align-items-center gap-2">
                <div
                    v-if="!props.showMobileHeader || props.icon || props.titulo || props.subtitulo"
                    class="w-100 d-flex align-items-center justify-content-between gap-3"
                >
                    <div class="d-flex align-items-center gap-2">
                        <BotaoVoltar v-if="!props.showMobileHeader" :icon-style="{ color: 'var(--color-text-light)' }"/>

                        <div v-if="props.icon" class="icon-container small">
                            <i :class="props.icon" style="font-size: 20px;"/>
                        </div>

                        <div class="d-flex flex-column gap-1">
                            <h2 class="header-title" style="font-size: 1.3rem;">
                                {{ props.titulo }}
                            </h2>

                            <span v-if="props.subtitulo" style="font-weight: 300; text-align: justify;">
                                {{ props.subtitulo }}
                            </span>
                        </div>

                        <i
                            v-if="props.linkSaibaMais"
                            v-tooltip="'Saiba Mais'"
                            class="pi pi-question-circle icon-link-saiba-mais icon-link-saiba-mais-mobile"
                            @click="acessarLinkSaibaMais()"
                        />
                    </div>
                </div>

                <slot/>
            </div>
        </div>
        <!-- FIM MOBILE -->

        <!-- MOBILE TIPO ACCORDION -->
        <Accordion v-if="isMobile && props.accordion" v-model:value="activeAccordion">
            <AccordionPanel
                value="1"
                :pt:root:style="{
                    background: 'var(--color-primary)',
                    border: 'none',
                    borderRadius: '8px'
                }"
            >
                <AccordionHeader
                    :pt:root:style="{
                        background: 'var(--color-primary)',
                        color: 'var(--color-text-light)'
                    }"
                    :pt:toggleicon:style="{ color: 'var(--color-text-light)' }"
                >
                    <div class="d-flex align-items-center justify-content-start gap-2">
                        <span style="margin: 0; font-weight: 400">
                            {{ props.accordionTitle }}
                        </span>

                        <Badge
                            v-show="props.accordionBagdeValue > 0"
                            :value="props.accordionBagdeValue"
                            severity="danger"
                            :pt:root:style="{ fontSize: '1rem' }"
                        />
                    </div>
                </AccordionHeader>

                <AccordionContent
                    :pt:content:style="{
                        background: 'var(--color-primary)',
                        color: 'var(--color-text-light)',
                        borderRadius: '8px'
                    }"
                >
                    <div class="w-100 d-flex flex-column align-items-center">
                        <slot/>
                    </div>
                </AccordionContent>
            </AccordionPanel>
        </Accordion>
        <!-- FIM MOBILE TIPO ACCORDION -->
    </header>
</template>

<style scoped>
.header-container {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.header {
    background: var(--color-primary);
    color: var(--color-text-light);
}

.desktop-header {
    min-height: 130px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 30px;
}

.header-title {
    margin: 0;
    font-weight: 600;
}

.icon-container {
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    color: var(--color-text-light);
}

.icon-container i {
    font-size: 23px;
}

.icon-container.small {
    width: 30px;
    height: 30px;
}

.icon-container.small i {
    font-size: 20px;
}

.icon-link-saiba-mais {
    margin-left: 10px;
    font-size: 1.3rem;
    cursor: pointer;
}

.icon-link-saiba-mais-mobile {
    position: absolute;
    right: 15px;
}
</style>
